import { DEFAULT_ACCOUNT_CONFIG } from '@course-authoring/shared-types';

/** In-session reveal history; completion and viewed progress remain separate. */
export const DEFAULT_BLOCK_PACING_DURATION_MS = DEFAULT_ACCOUNT_CONFIG.contentPacingDuration * 1000;
export type BlockPacingMode = 'unresolved' | 'off' | 'positive';

export interface BlockPacing {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => number;
  getMode: () => BlockPacingMode;
  resolve: (durationMs: number) => void;
  activate: () => void;
  isReleased: (blockId: string) => boolean;
  wasPaced: (blockId: string) => boolean;
  deadline: (blockId: string) => number | undefined;
  clear: (blockId: string) => void;
  revealThrough: (blockIds: readonly string[], targetId: string) => void;
  reconcile: () => void;
  dispose: () => void;
}

export function createBlockPacing(initialDurationMs: number | null = DEFAULT_BLOCK_PACING_DURATION_MS): BlockPacing {
  const released = new Set<string>();
  const paced = new Set<string>();
  const deadlines = new Map<string, number>();
  const listeners = new Set<() => void>();
  let durationMs: number | null = initialDurationMs === null
    ? null
    : Number.isFinite(initialDurationMs) && initialDurationMs > 0
      ? initialDurationMs
      : 0;
  let revision = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let active = true;
  const getMode = (): BlockPacingMode => durationMs === null ? 'unresolved' : durationMs > 0 ? 'positive' : 'off';
  const notify = (): void => {
    if (!active) return;
    revision += 1;
    listeners.forEach((listener) => listener());
  };
  const schedule = (): void => {
    clearTimeout(timer);
    timer = undefined;
    if (active && deadlines.size) {
      timer = setTimeout(reconcile, Math.max(0, Math.min(...deadlines.values()) - Date.now()));
    }
  };
  const reconcile = (): void => {
    if (!active) return;
    let changed = false;
    for (const [id, deadline] of deadlines) {
      if (deadline > Date.now()) continue;
      deadlines.delete(id);
      released.add(id);
      paced.add(id);
      changed = true;
    }
    schedule();
    if (changed) notify();
  };
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    getSnapshot: () => revision,
    getMode,
    resolve(nextDurationMs) {
      if (!active || durationMs !== null) return;
      durationMs = Number.isFinite(nextDurationMs) && nextDurationMs > 0 ? nextDurationMs : 0;
      notify();
    },
    activate() {
      if (active) return;
      active = true;
      reconcile();
      schedule();
    },
    isReleased: (id) => released.has(id),
    wasPaced: (id) => paced.has(id),
    deadline: (id) => deadlines.get(id),
    clear(id) {
      if (!active || getMode() !== 'positive' || released.has(id) || deadlines.has(id)) return;
      deadlines.set(id, Date.now() + durationMs!);
      schedule();
      notify();
    },
    revealThrough(ids, targetId) {
      if (!active) return;
      const target = ids.indexOf(targetId);
      let changed = false;
      for (let i = 0; i < target; i += 1) {
        const id = ids[i];
        if (id === undefined || released.has(id)) continue;
        deadlines.delete(id);
        released.add(id);
        changed = true;
      }
      if (changed) {
        schedule();
        notify();
      }
    },
    reconcile,
    dispose() {
      active = false;
      clearTimeout(timer);
      timer = undefined;
      listeners.clear();
    },
  };
}

export function pacedBlockCount(ids: readonly string[], pacing: BlockPacing): number {
  if (pacing.getMode() === 'off') return ids.length;
  const boundary = ids.findIndex((id) => !pacing.isReleased(id));
  return boundary < 0 ? ids.length : boundary + 1;
}
